import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
	DndContext,
	closestCenter,
	KeyboardSensor,
	PointerSensor,
	useSensor,
	useSensors,
	type DragEndEvent,
} from '@dnd-kit/core'
import {
	arrayMove,
	SortableContext,
	sortableKeyboardCoordinates,
	useSortable,
	verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import AdminPanelCN from 'components/AdminPanelCN/AdminPanelCN'
import HeaderCN from 'components/HeaderCN/HeaderCN'
import { Button } from 'components/ui/button'
import { Input } from 'components/ui/input'
import { Textarea } from 'components/ui/textarea'
import { Checkbox } from 'components/ui/checkbox'
import { Badge } from 'components/ui/badge'
import { Select } from 'components/ui/select'
import {
	initializeTemplates,
	initializeTemplateProsCons,
	createTemplate,
	updateTemplate,
	deleteTemplate,
	createTemplateProCon,
	deleteTemplateProCon,
} from 'slices/templateSlice'
import {
	GripVertical,
	Plus,
	Trash2,
	LayoutTemplate,
	Loader2,
	ThumbsUp,
	ThumbsDown,
} from 'lucide-react'
import notify from 'components/ui/notify'

// ─── Types ──────────────────────────────────────────────────────────────────────

interface ProConData {
	_id: string
	type: 'PRO' | 'CON'
	category: string
	description: string
	defaultImpactLevel: string
	isNew?: boolean
}

const IMPACT_OPTIONS = ['', 'HIGH', 'MEDIUM', 'LOW']

// ─── SortableRow ────────────────────────────────────────────────────────────────

interface SortableRowProps {
	id: string
	seq: number
	item: ProConData
	onTypeChange: (id: string, value: 'PRO' | 'CON') => void
	onCategoryChange: (id: string, value: string) => void
	onDescriptionChange: (id: string, value: string) => void
	onImpactChange: (id: string, value: string) => void
	onDelete: (id: string) => void
	isSelected: boolean
	onSelect: (id: string, checked: boolean) => void
}

const SortableRow = ({
	id,
	seq,
	item,
	onTypeChange,
	onCategoryChange,
	onDescriptionChange,
	onImpactChange,
	onDelete,
	isSelected,
	onSelect,
}: SortableRowProps) => {
	const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id })

	const style = {
		transform: CSS.Transform.toString(transform),
		transition,
		zIndex: isDragging ? 10 : undefined,
		opacity: isDragging ? 0.8 : 1,
	}

	const isPro = item.type === 'PRO'
	const tintClass = isDragging
		? 'bg-blue-50 shadow-md rounded'
		: isPro
			? 'bg-emerald-50/40 hover:bg-emerald-50/70'
			: 'bg-rose-50/40 hover:bg-rose-50/70'

	return (
		<div
			ref={setNodeRef}
			style={style}
			className={`grid grid-cols-[24px_32px_90px_140px_1fr_110px_32px_32px] gap-1.5 py-1 px-1.5 items-center border-b border-gray-100 last:border-b-0 ${tintClass}`}
		>
			<div
				{...attributes}
				{...listeners}
				className="flex items-center justify-center cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600"
			>
				<GripVertical className="w-3.5 h-3.5" />
			</div>

			<div className="text-xs text-gray-500 text-center font-medium">{seq}</div>

			<Select
				value={item.type}
				onChange={e => onTypeChange(id, (e.target.value as 'PRO' | 'CON') || 'PRO')}
				className={`h-7 text-xs font-semibold ${
					isPro ? 'text-emerald-700' : 'text-rose-700'
				}`}
			>
				<option value="PRO">PRO</option>
				<option value="CON">CON</option>
			</Select>

			<Input
				value={item.category}
				onChange={e => onCategoryChange(id, e.target.value)}
				className="h-7 text-xs"
				placeholder="Category (e.g. Anchor Tenant)"
			/>

			<Input
				value={item.description}
				onChange={e => onDescriptionChange(id, e.target.value)}
				className="h-7 text-xs"
				placeholder="Description (e.g. Anchored by Whole Foods)"
			/>

			<Select
				value={item.defaultImpactLevel}
				onChange={e => onImpactChange(id, e.target.value)}
				className="h-7 text-xs"
			>
				<option value="">—</option>
				<option value="HIGH">High</option>
				<option value="MEDIUM">Medium</option>
				<option value="LOW">Low</option>
			</Select>

			<div className="flex items-center justify-center">
				<Checkbox
					checked={isSelected}
					onCheckedChange={checked => onSelect(id, !!checked)}
				/>
			</div>

			<button
				type="button"
				title="Delete item"
				onClick={() => onDelete(id)}
				className="flex items-center justify-center bg-transparent border-none cursor-pointer text-gray-400 hover:text-red-500 transition-colors p-0"
			>
				<Trash2 className="w-3.5 h-3.5" />
			</button>
		</div>
	)
}

// ─── Main Component ─────────────────────────────────────────────────────────────

const ProsConsTemplateCN = () => {
	const dispatch = useDispatch()
	const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

	const { entities: templateEntities, ids: templateIds, isInitialized } = useSelector(
		(state: any) => state.templates,
	)
	const {
		entities: itemEntities,
		ids: itemIds,
		isLoading: itemsLoading,
	} = useSelector((state: any) => state.templates.prosCons)

	const prosConsTemplates = useMemo(() => {
		const all = (templateIds || []).map((id: string) => templateEntities[id]).filter(Boolean)
		return all
			.filter((t: any) => t.templateType === 'prosCons')
			.sort((a: any, b: any) =>
				(a.templateName || '').localeCompare(b.templateName || ''),
			)
	}, [templateIds, templateEntities])

	// ─── Editor State ───────────────────────────────────────────────────────────

	const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null)
	const [isCreateMode, setIsCreateMode] = useState(false)
	const [templateName, setTemplateName] = useState('')
	const [description, setDescription] = useState('')
	const [accessLevel, setAccessLevel] = useState('private')
	const [errors, setErrors] = useState<Record<string, string>>({})
	const [itemOrder, setItemOrder] = useState<string[]>([])
	const [itemsMap, setItemsMap] = useState<Record<string, ProConData>>({})
	const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set())
	const [isDirty, setIsDirty] = useState(false)
	const [isSaving, setIsSaving] = useState(false)
	const [originalItemIds, setOriginalItemIds] = useState<Set<string>>(new Set())
	const nextTempId = useRef(0)

	// ─── Init ───────────────────────────────────────────────────────────────────

	useEffect(() => {
		;(dispatch as any)(initializeTemplates())
	}, [dispatch])

	useEffect(() => {
		if (selectedTemplateId) {
			;(dispatch as any)(initializeTemplateProsCons(selectedTemplateId))
		}
	}, [dispatch, selectedTemplateId])

	useEffect(() => {
		if (!selectedTemplateId || isCreateMode) return
		const template = templateEntities[selectedTemplateId]
		if (!template) return

		setTemplateName(template.templateName || '')
		setDescription(template.description || '')
		setAccessLevel(template.accessLevel || 'private')

		const loaded = (itemIds || [])
			.map((id: string) => itemEntities[id])
			.filter(Boolean)
			.sort((a: any, b: any) => (a.seq || 0) - (b.seq || 0))

		const order: string[] = []
		const map: Record<string, ProConData> = {}
		const origIds = new Set<string>()
		loaded.forEach((it: any) => {
			order.push(it._id)
			map[it._id] = {
				_id: it._id,
				type: it.type === 'CON' ? 'CON' : 'PRO',
				category: it.category || '',
				description: it.description || '',
				defaultImpactLevel: it.defaultImpactLevel || '',
			}
			origIds.add(it._id)
		})
		setItemOrder(order)
		setItemsMap(map)
		setOriginalItemIds(origIds)
		setSelectedItems(new Set())
		setErrors({})
		setIsDirty(false)
	}, [itemIds, itemEntities, selectedTemplateId, isCreateMode, templateEntities])

	// ─── DnD ────────────────────────────────────────────────────────────────────

	const sensors = useSensors(
		useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
		useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
	)

	const handleDragEnd = (event: DragEndEvent) => {
		const { active, over } = event
		if (over && active.id !== over.id) {
			setItemOrder(prev => {
				const oldIndex = prev.indexOf(active.id as string)
				const newIndex = prev.indexOf(over.id as string)
				return arrayMove(prev, oldIndex, newIndex)
			})
			setIsDirty(true)
		}
	}

	// ─── Row Handlers ───────────────────────────────────────────────────────────

	const patchItem = (id: string, patch: Partial<ProConData>) => {
		setItemsMap(prev => ({ ...prev, [id]: { ...prev[id], ...patch } }))
		setIsDirty(true)
	}

	const handleSelect = (id: string, checked: boolean) => {
		const newSet = new Set(selectedItems)
		if (checked) newSet.add(id)
		else newSet.delete(id)
		setSelectedItems(newSet)
	}

	const handleSelectAll = (checked: boolean) => {
		setSelectedItems(checked ? new Set(itemOrder) : new Set())
	}

	const isAllSelected =
		itemOrder.length > 0 && itemOrder.every(id => selectedItems.has(id))

	const handleDeleteItem = (id: string) => {
		const item = itemsMap[id]
		const label = item?.description?.trim() || 'this item'
		notify({
			title: 'Delete Item',
			text: `Are you sure you want to delete "${label}"?`,
			icon: 'warning',
			buttons: ['Cancel', 'Delete'],
			dangerMode: true,
		} as any).then((value: any) => {
			if (value) {
				setItemOrder(prev => prev.filter(iid => iid !== id))
				setItemsMap(prev => {
					const next = { ...prev }
					delete next[id]
					return next
				})
				setSelectedItems(prev => {
					const next = new Set(prev)
					next.delete(id)
					return next
				})
				setIsDirty(true)
			}
		})
	}

	const handleDeleteSelected = () => {
		if (selectedItems.size === 0) return
		notify({
			title: 'Delete Selected Items',
			text: `Are you sure you want to delete ${selectedItems.size} item${
				selectedItems.size > 1 ? 's' : ''
			}?`,
			icon: 'warning',
			buttons: ['Cancel', 'Delete'],
			dangerMode: true,
		} as any).then((value: any) => {
			if (value) {
				setItemOrder(prev => prev.filter(id => !selectedItems.has(id)))
				setItemsMap(prev => {
					const next = { ...prev }
					selectedItems.forEach(id => delete next[id])
					return next
				})
				setSelectedItems(new Set())
				setIsDirty(true)
			}
		})
	}

	const handleAddPro = () => {
		addNew('PRO')
	}

	const handleAddCon = () => {
		addNew('CON')
	}

	const addNew = (type: 'PRO' | 'CON') => {
		const tempId = `_new_${nextTempId.current++}`
		const newItem: ProConData = {
			_id: tempId,
			type,
			category: '',
			description: '',
			defaultImpactLevel: '',
			isNew: true,
		}
		setItemOrder(prev => [...prev, tempId])
		setItemsMap(prev => ({ ...prev, [tempId]: newItem }))
		setIsDirty(true)
	}

	// ─── Template List Handlers ─────────────────────────────────────────────────

	const switchTemplate = useCallback((templateId: string | null, createMode: boolean) => {
		setSelectedTemplateId(templateId)
		setIsCreateMode(createMode)
		if (createMode) {
			setTemplateName('')
			setDescription('')
			setAccessLevel('private')
			setItemOrder([])
			setItemsMap({})
			setOriginalItemIds(new Set())
			setSelectedItems(new Set())
			setErrors({})
			setIsDirty(false)
		}
	}, [])

	const handleTemplateClick = useCallback(
		(templateId: string) => {
			if (templateId === selectedTemplateId) return
			const doSwitch = () => switchTemplate(templateId, false)
			if (isDirty) {
				;notify({
					text: 'You have unsaved changes. Discard them?',
					buttons: ['Cancel', 'Discard'],
					dangerMode: true,
				}).then((value: any) => {
					if (value) doSwitch()
				})
			} else {
				doSwitch()
			}
		},
		[selectedTemplateId, isDirty, switchTemplate],
	)

	const handleNewTemplate = useCallback(() => {
		const doSwitch = () => switchTemplate(null, true)
		if (isDirty) {
			;notify({
				text: 'You have unsaved changes. Discard them?',
				buttons: ['Cancel', 'Discard'],
				dangerMode: true,
			}).then((value: any) => {
				if (value) doSwitch()
			})
		} else {
			doSwitch()
		}
	}, [isDirty, switchTemplate])

	const handleDeleteTemplate = useCallback(
		(e: React.MouseEvent, templateId: string) => {
			e.stopPropagation()
			const template = templateEntities[templateId]
			;notify({
				text: `Delete template "${template?.templateName}"? This cannot be undone.`,
				buttons: ['Cancel', 'Delete'],
				dangerMode: true,
			}).then(async (value: any) => {
				if (value) {
					await (dispatch as any)(deleteTemplate(templateId))
					;(dispatch as any)(initializeTemplates())
					if (selectedTemplateId === templateId) {
						switchTemplate(null, false)
					}
				}
			})
		},
		[dispatch, templateEntities, selectedTemplateId, switchTemplate],
	)

	// ─── Save ───────────────────────────────────────────────────────────────────

	const validate = () => {
		const newErrors: Record<string, string> = {}
		if (!templateName.trim()) newErrors.templateName = 'Template name is required'
		if (!description.trim()) newErrors.description = 'Description is required'
		const emptyDescription = itemOrder.find(id => !itemsMap[id]?.description.trim())
		if (emptyDescription) newErrors.items = 'All items must have a description'
		const emptyCategory = itemOrder.find(id => !itemsMap[id]?.category.trim())
		if (emptyCategory) newErrors.items = 'All items must have a category'
		setErrors(newErrors)
		return Object.keys(newErrors).length === 0
	}

	const handleSave = async () => {
		if (!validate()) return
		setIsSaving(true)

		try {
			if (isCreateMode) {
				const result = await (dispatch as any)(
					createTemplate({
						templateName: templateName.trim(),
						description: description.trim(),
						templateType: 'prosCons',
						accessLevel,
					}),
				)
				const newTemplateId = result.payload?._id
				if (!newTemplateId) throw new Error('Failed to create template')

				for (let i = 0; i < itemOrder.length; i++) {
					const it = itemsMap[itemOrder[i]]
					await (dispatch as any)(
						createTemplateProCon({
							templateId: newTemplateId,
							seq: i + 1,
							type: it.type,
							category: it.category.trim(),
							description: it.description.trim(),
							defaultImpactLevel: it.defaultImpactLevel || null,
						}),
					)
				}

				await (dispatch as any)(initializeTemplates())
				switchTemplate(newTemplateId, false)
				notify('Created!', `Template "${templateName.trim()}" has been created.`, 'success')
			} else if (selectedTemplateId) {
				await (dispatch as any)(
					updateTemplate({
						_id: selectedTemplateId,
						templateName: templateName.trim(),
						description: description.trim(),
						accessLevel,
					}),
				)

				for (const origId of originalItemIds) {
					await (dispatch as any)(
						deleteTemplateProCon({
							templateId: selectedTemplateId,
							_id: origId,
						}),
					)
				}
				for (let i = 0; i < itemOrder.length; i++) {
					const it = itemsMap[itemOrder[i]]
					await (dispatch as any)(
						createTemplateProCon({
							templateId: selectedTemplateId,
							seq: i + 1,
							type: it.type,
							category: it.category.trim(),
							description: it.description.trim(),
							defaultImpactLevel: it.defaultImpactLevel || null,
						}),
					)
				}

				await (dispatch as any)(initializeTemplates())
				await (dispatch as any)(initializeTemplateProsCons(selectedTemplateId))
				notify('Saved!', `Template "${templateName.trim()}" has been updated.`, 'success')
			}
			setIsDirty(false)
		} catch (err: any) {
			notify('Error', err.message || 'Failed to save template', 'error')
		} finally {
			setIsSaving(false)
		}
	}

	// ─── Derived ────────────────────────────────────────────────────────────────

	const isEditorActive = isCreateMode || selectedTemplateId !== null
	const isSaveDisabled =
		!templateName.trim() || !description.trim() || itemOrder.length === 0 || isSaving

	const proCount = useMemo(
		() => itemOrder.filter(id => itemsMap[id]?.type === 'PRO').length,
		[itemOrder, itemsMap],
	)
	const conCount = itemOrder.length - proCount

	// ─── Render ─────────────────────────────────────────────────────────────────

	return (
		<div className="flex h-screen overflow-hidden bg-gray-50">
			<AdminPanelCN
				mobileOpen={mobileMenuOpen}
				onMobileClose={() => setMobileMenuOpen(false)}
			/>

			<div className="flex-1 flex flex-col overflow-hidden">
				<HeaderCN onMobileMenuToggle={() => setMobileMenuOpen(!mobileMenuOpen)} />

				<div className="flex-1 flex overflow-hidden">
					<main className="flex-1 overflow-y-auto p-6">
						<h2 className="text-2xl font-semibold text-gray-800 mb-4">
							Pros &amp; Cons Templates
						</h2>

						<div className="max-w-[1180px] mx-auto w-full mt-4 flex flex-row gap-4 min-h-[600px]">
							{/* ── Left Panel: Template List ────────────────────────────── */}
							<div className="w-[260px] shrink-0 bg-white rounded-lg border border-gray-200 flex flex-col">
								<div className="px-3 py-2.5 border-b border-gray-200 flex items-center justify-between">
									<span className="text-sm font-semibold text-gray-700">Templates</span>
									<span className="text-xs text-gray-400">{prosConsTemplates.length}</span>
								</div>

								<div className="flex-1 overflow-y-auto">
									{!isInitialized ? (
										<div className="flex items-center justify-center py-10">
											<Loader2 className="w-5 h-5 animate-spin text-gray-400" />
										</div>
									) : prosConsTemplates.length === 0 ? (
										<div className="py-10 text-center text-sm text-gray-400">
											No templates yet
										</div>
									) : (
										prosConsTemplates.map((template: any) => (
											<div
												key={template._id}
												onClick={() => handleTemplateClick(template._id)}
												className={`group flex items-center justify-between px-3 py-2 cursor-pointer border-l-2 transition-colors ${
													selectedTemplateId === template._id
														? 'bg-[#0080fe]/5 text-[#0080fe] border-[#0080fe] font-semibold'
														: 'border-transparent hover:bg-gray-50 text-gray-700'
												}`}
											>
												<div className="min-w-0 flex-1">
													<div className="text-sm truncate">{template.templateName}</div>
													{template.description && (
														<div className="text-[11px] text-gray-400 truncate mt-0.5">
															{template.description}
														</div>
													)}
												</div>
												<button
													type="button"
													title="Delete template"
													onClick={e => handleDeleteTemplate(e, template._id)}
													className="opacity-0 group-hover:opacity-100 bg-transparent border-none cursor-pointer text-gray-400 hover:text-red-500 transition-all p-1 shrink-0"
												>
													<Trash2 className="w-3.5 h-3.5" />
												</button>
											</div>
										))
									)}
								</div>

								<div className="px-3 py-2 border-t border-gray-200">
									<Button
										variant="ghost"
										onClick={handleNewTemplate}
										className="w-full justify-start"
									>
										<Plus className="w-4 h-4 mr-1.5" />
										New Template
									</Button>
								</div>
							</div>

							{/* ── Right Panel: Template Editor ─────────────────────────── */}
							<div className="flex-1 bg-white rounded-lg border border-gray-200 p-5 flex flex-col">
								{!isEditorActive ? (
									<div className="flex-1 flex flex-col items-center justify-center text-gray-400">
										<LayoutTemplate className="w-12 h-12 mb-3 text-gray-300" />
										<p className="text-sm">Select a template or create a new one</p>
									</div>
								) : (
									<>
										{/* Header Fields */}
										<div className="flex flex-col gap-2 mb-3">
											<div>
												<label className="block text-xs font-medium text-gray-700 mb-1">
													Template Name <span className="text-red-500">*</span>
												</label>
												<Input
													value={templateName}
													onChange={e => {
														setTemplateName(e.target.value)
														setIsDirty(true)
														if (errors.templateName)
															setErrors({ ...errors, templateName: '' })
													}}
													className={`text-xs h-8 ${
														errors.templateName ? 'border-red-500' : ''
													}`}
													placeholder="e.g. Retail site pros & cons starter"
												/>
												{errors.templateName && (
													<p className="text-red-500 text-[0.7rem] mt-0.5">
														{errors.templateName}
													</p>
												)}
											</div>
											<div>
												<label className="block text-xs font-medium text-gray-700 mb-1">
													Description <span className="text-red-500">*</span>
												</label>
												<Textarea
													value={description}
													onChange={e => {
														setDescription(e.target.value)
														setIsDirty(true)
														if (errors.description)
															setErrors({ ...errors, description: '' })
													}}
													className={`text-xs min-h-[3.5rem] ${
														errors.description ? 'border-red-500' : ''
													}`}
													placeholder="What kind of deal is this pros/cons starter for?"
													rows={2}
												/>
												{errors.description && (
													<p className="text-red-500 text-[0.7rem] mt-0.5">
														{errors.description}
													</p>
												)}
											</div>
										</div>

										{/* Access Level + Counts */}
										<div className="flex flex-row gap-4 mb-3 p-2 bg-gray-50 rounded text-xs items-center flex-wrap">
											<span className="font-semibold text-gray-700 mr-2">Access:</span>
											{['private', 'org-wide', 'public'].map(level => (
												<label key={level} className="flex items-center gap-1.5 cursor-pointer">
													<input
														type="radio"
														name="accessLevel"
														value={level}
														checked={accessLevel === level}
														onChange={e => {
															setAccessLevel(e.target.value)
															setIsDirty(true)
														}}
														className="w-3.5 h-3.5 text-blue-600 cursor-pointer"
													/>
													{level === 'private'
														? 'Private'
														: level === 'org-wide'
															? 'Org-Wide'
															: 'Public'}
												</label>
											))}
											<div className="flex-1" />
											<Badge className="text-[10px] bg-emerald-100 text-emerald-700 flex items-center gap-1">
												<ThumbsUp className="w-3 h-3" />
												{proCount} pro{proCount === 1 ? '' : 's'}
											</Badge>
											<Badge className="text-[10px] bg-rose-100 text-rose-700 flex items-center gap-1">
												<ThumbsDown className="w-3 h-3" />
												{conCount} con{conCount === 1 ? '' : 's'}
											</Badge>
										</div>

										{/* Items Table */}
										<div className="flex-1 overflow-y-auto border border-gray-200 rounded mb-3">
											<div className="grid grid-cols-[24px_32px_90px_140px_1fr_110px_32px_32px] gap-1.5 px-1.5 py-1.5 border-b border-gray-200 bg-gray-50 font-semibold text-xs sticky top-0 z-10">
												<span></span>
												<span className="text-center">Seq</span>
												<span>Type</span>
												<span>Category</span>
												<span>Description</span>
												<span>Impact</span>
												<label className="flex items-center justify-center cursor-pointer">
													<Checkbox
														checked={isAllSelected}
														onCheckedChange={checked => handleSelectAll(!!checked)}
													/>
												</label>
												<span></span>
											</div>

											{itemsLoading && !isCreateMode ? (
												<div className="flex items-center justify-center py-8">
													<Loader2 className="w-5 h-5 animate-spin text-gray-400" />
												</div>
											) : itemOrder.length === 0 ? (
												<div className="py-8 text-center text-sm text-gray-400">
													No items yet. Click "+ Add Pro" or "+ Add Con" to start.
												</div>
											) : (
												<DndContext
													sensors={sensors}
													collisionDetection={closestCenter}
													onDragEnd={handleDragEnd}
												>
													<SortableContext
														items={itemOrder}
														strategy={verticalListSortingStrategy}
													>
														{itemOrder.map((id, index) => {
															const item = itemsMap[id]
															if (!item) return null
															return (
																<SortableRow
																	key={id}
																	id={id}
																	seq={index + 1}
																	item={item}
																	onTypeChange={(iid, v) => patchItem(iid, { type: v })}
																	onCategoryChange={(iid, v) => patchItem(iid, { category: v })}
																	onDescriptionChange={(iid, v) =>
																		patchItem(iid, { description: v })
																	}
																	onImpactChange={(iid, v) =>
																		patchItem(iid, { defaultImpactLevel: v })
																	}
																	onDelete={handleDeleteItem}
																	isSelected={selectedItems.has(id)}
																	onSelect={handleSelect}
																/>
															)
														})}
													</SortableContext>
												</DndContext>
											)}
											{errors.items && (
												<p className="text-red-500 text-[0.7rem] px-3 py-1">
													{errors.items}
												</p>
											)}
										</div>

										{/* Actions */}
										<div className="flex items-center justify-between gap-2">
											<div className="flex items-center gap-2">
												<Button variant="ghost" onClick={handleAddPro} className="text-emerald-700">
													<ThumbsUp className="w-4 h-4 mr-1" />
													Add Pro
												</Button>
												<Button variant="ghost" onClick={handleAddCon} className="text-rose-700">
													<ThumbsDown className="w-4 h-4 mr-1" />
													Add Con
												</Button>
												{selectedItems.size > 0 && (
													<Button
														variant="ghost-red"
														onClick={handleDeleteSelected}
													>
														<Trash2 className="w-4 h-4 mr-1" />
														Delete Selected ({selectedItems.size})
													</Button>
												)}
											</div>
											<Button onClick={handleSave} disabled={isSaveDisabled}>
												{isSaving ? (
													<>
														<Loader2 className="w-4 h-4 mr-1 animate-spin" />
														Saving...
													</>
												) : (
													<>{isCreateMode ? 'Create Template' : 'Save Template'}</>
												)}
											</Button>
										</div>
									</>
								)}
							</div>
						</div>
					</main>
				</div>
			</div>
		</div>
	)
}

export default ProsConsTemplateCN
